import { atom, selector, useRecoilCallback } from 'recoil';
import { api, getDwellTimes } from '@/repositories/HttpRepository';
import _, { debounce } from 'lodash';

/* global BigInt */

const zoomToResolution = {
  20: 10,
  19: 10,
  18: 10,
  17: 10,
  16: 10,
  15: 10,
  14: 10,
  13: 9,
  12: 9,
  11: 8,
  10: 8,
  9: 7,
  8: 6,
  7: 6,
  6: 5,
  5: 4,
  4: 4,
  3: 4,
  2: 4,
  1: 4,
  0: 4,
};

export const mapViewport = atom({
  key: 'map_viewport',
  default: null,
});

export const upcomingDwelltimeViewport = atom({
  key: 'upcoming_viewport',
});

export const dwellTimes = atom({
  key: 'atom-dwell-time-stats',
  default: {},
});

function longToHex(num) {
  const big = BigInt(num);
  return big.toString(16);
}

export const dwellTimeResolutionSelector = selector({
  key: 'selector-dwell-time',
  get: ({ get }) => {
    const viewport = get(mapViewport);
    // todo parser
    if (!viewport) return null;
    const bounds = [
      [viewport.bounds[0][1], viewport.bounds[0][0]],
      [viewport.bounds[1][1], viewport.bounds[1][0]],
    ];
    const resolution = zoomToResolution[viewport.zoom.toFixed(0)];
    return {
      resolution,
      bounds,
    };
  },
});

let upcoming = {};

export const useViewPortUpdate = () => {
  const callback = useRecoilCallback(({ snapshot, set, reset }) => async () => {
    const currentView = await snapshot.getPromise(dwellTimeResolutionSelector);
    if (!currentView) return;
    const { resolution, bounds } = currentView;

    // const upcoming = await snapshot.getPromise(upcomingDwelltimeViewport);
    if (upcoming && upcoming.resolution === resolution) {
      if (
        bounds[0][0] >= upcoming.bounds[0][0] &&
        bounds[0][1] >= upcoming.bounds[0][1] &&
        bounds[1][0] <= upcoming.bounds[1][0] &&
        bounds[1][1] <= upcoming.bounds[1][1]
      ) {
        return;
      }
    }
    const latGap = bounds[1][0] - bounds[0][0];
    const lngGap = bounds[1][1] - bounds[0][1];
    const collectingBound = [
      [Math.max(20, bounds[0][0] - latGap * 0.5), Math.min(bounds[0][1] - lngGap * 0.5, -10)],
      [bounds[1][0] + latGap * 0.5, Math.min(-10, bounds[1][1] + lngGap * 0.5)],
    ];

    upcoming = { resolution, bounds: collectingBound };

    const current = await snapshot.getPromise(dwellTimes);
    if (current) {
      if (resolution === current.resolution) {
        if (
          bounds[0][0] >= current.bounds[0][0] &&
          bounds[0][1] >= current.bounds[0][1] &&
          bounds[1][0] <= current.bounds[1][0] &&
          bounds[1][1] <= current.bounds[1][1]
        ) {
          return;
        }
      }
    }

    const data = await getDwellTimes(resolution, collectingBound).then((r) => {
      return r.data
        .map((d) =>
          ({
            hex: longToHex(d.subject.split('_')[1]),
            count: parseInt(d.attributes.R0_count, 10),
            med: parseFloat(d.attributes.R0_med),
            avg:
              parseInt(d.attributes.R0_count, 10) < 1
                ? 0.0
                : parseFloat(d.attributes.R0_sum) / parseInt(d.attributes.R0_count, 10),
            lat: d.attributes.lat,
            lng: d.attributes.lng,
          })
        )
        .filter((d) => d.count);
    });
    const updated = await snapshot.getPromise(dwellTimeResolutionSelector);
    if (updated.resolution === resolution) set(dwellTimes, { data, resolution, bounds: collectingBound });
  });
  return callback;
};
